/**
 * GeoMap — Choropleth map with country selector.
 * 
 * US-only customers: shows US state map.
 * International customers: shows US map by default + dropdown to switch to
 * any country that has employees. Fetches state/province data on-demand.
 * 
 * Click a state to see work location codes, names, and counts.
 */
import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import * as topojson from 'topojson-client'
import './GeoMap.css'
import LoadError from '../LoadError'
import { loadErrorMessage } from '../../utils/loadState'
import { API_BASE as API_BASE_CONFIG } from '../../config'

// ═══ FIPS → US state abbreviation ═══
const FIPS = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT","10":"DE","11":"DC","12":"FL",
  "13":"GA","15":"HI","16":"ID","17":"IL","18":"IN","19":"IA","20":"KS","21":"KY","22":"LA","23":"ME",
  "24":"MD","25":"MA","26":"MI","27":"MN","28":"MS","29":"MO","30":"MT","31":"NE","32":"NV","33":"NH",
  "34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND","39":"OH","40":"OK","41":"OR","42":"PA","44":"RI",
  "45":"SC","46":"SD","47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA","54":"WV","55":"WI","56":"WY"
}
const US_STATES_SET = new Set(Object.values(FIPS))

// ═══ US State full names ═══
const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',DC:'District of Columbia',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',
  LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',
  MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',
  OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',
  WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming'
}

// ═══ Canadian province name (from GeoJSON) → abbreviation ═══
const PROVINCE_ABBR = {
  'Ontario':'ON','Quebec':'QC','British Columbia':'BC','Alberta':'AB',
  'Saskatchewan':'SK','Manitoba':'MB','Nova Scotia':'NS','New Brunswick':'NB',
  'Newfoundland and Labrador':'NL','Prince Edward Island':'PE',
  'Northwest Territories':'NT','Yukon Territory':'YT','Nunavut':'NU',
}
// Reverse: abbr → name
const PROVINCE_NAMES = Object.fromEntries(Object.entries(PROVINCE_ABBR).map(([k,v]) => [v,k]))

// ═══ ISO alpha-3 → numeric (world-atlas TopoJSON uses numeric IDs) ═══
const ISO3_NUM = {
  AFG:'004',ALB:'008',DZA:'012',AGO:'024',ARG:'032',ARM:'051',AUS:'036',AUT:'040',
  AZE:'031',BHS:'044',BHR:'048',BGD:'050',BRB:'052',BLR:'112',BEL:'056',BOL:'068',
  BIH:'070',BWA:'072',BRA:'076',BRN:'096',BGR:'100',CAN:'124',CHL:'152',CHN:'156',
  COL:'170',COD:'180',CRI:'188',CIV:'384',HRV:'191',CUB:'192',CZE:'203',DNK:'208',
  DOM:'214',ECU:'218',EGY:'818',SLV:'222',EST:'233',ETH:'231',FIN:'246',FRA:'250',
  GAB:'266',DEU:'276',GHA:'288',GRC:'300',GTM:'320',GIN:'324',GUY:'328',HTI:'332',
  HND:'340',HUN:'348',ISL:'352',IND:'356',IDN:'360',IRN:'364',IRQ:'368',IRL:'372',
  ISR:'376',ITA:'380',JAM:'388',JPN:'392',JOR:'400',KAZ:'398',KEN:'404',KOR:'410',
  KWT:'414',LAO:'418',LVA:'428',LBN:'422',LBR:'430',LBY:'434',LTU:'440',LUX:'442',
  MYS:'458',MEX:'484',MDA:'498',MNG:'496',MNE:'499',MAR:'504',MOZ:'508',MMR:'104',
  NAM:'516',NPL:'524',NLD:'528',NZL:'554',NIC:'558',NER:'562',NGA:'566',MKD:'807',
  NOR:'578',OMN:'512',PAK:'586',PAN:'591',PNG:'598',PRY:'600',PER:'604',PHL:'608',
  POL:'616',PRT:'620',QAT:'634',ROU:'642',RUS:'643',RWA:'646',SAU:'682',SEN:'686',
  SRB:'688',SGP:'702',SVK:'703',SVN:'705',SOM:'706',ZAF:'710',SSD:'728',ESP:'724',
  LKA:'144',SDN:'729',SUR:'740',SWE:'752',CHE:'756',SYR:'760',TWN:'158',TZA:'834',
  THA:'764',TGO:'768',TTO:'780',TUN:'788',TUR:'792',UGA:'800',UKR:'804',ARE:'784',
  GBR:'826',USA:'840',URY:'858',UZB:'860',VEN:'862',VNM:'704',YEM:'887',ZMB:'894',
  ZWE:'716',ATA:'010',BEN:'204',BFA:'854',BDI:'108',KHM:'116',CMR:'120',CAF:'140',
  TCD:'148',COM:'174',COG:'178',CYP:'196',DJI:'262',GNQ:'226',ERI:'232',SWZ:'748',
  FJI:'242',GMB:'270',GEO:'268',GRD:'308',GNB:'624',KGZ:'417',LSO:'426',
  LIE:'438',MDG:'450',MWI:'454',MDV:'462',MLI:'466',MLT:'470',MRT:'478',MUS:'480',
  FSM:'583',MCO:'492',NRU:'520',PLW:'585',WSM:'882',SMR:'674',STP:'678',SYC:'690',
  SLE:'694',SLB:'090',TJK:'762',TLS:'626',TON:'776',TKM:'795',TUV:'798',VUT:'548',
  VCT:'670',
}
const NUM_ISO3 = {}
Object.entries(ISO3_NUM).forEach(([a3, n]) => { NUM_ISO3[n] = a3 })

// Country display names
const COUNTRY_NAMES = {
  USA:'United States',CAN:'Canada',GBR:'United Kingdom',NLD:'Netherlands',
  BRA:'Brazil',AUS:'Australia',BEL:'Belgium',FRA:'France',DEU:'Germany',
  SGP:'Singapore',MYS:'Malaysia',QAT:'Qatar',ARE:'UAE',SAU:'Saudi Arabia',
  TTO:'Trinidad & Tobago',ATA:'Antarctica',IND:'India',CHN:'China',JPN:'Japan',
  MEX:'Mexico',ITA:'Italy',ESP:'Spain',KOR:'South Korea',
}

// ═══ Color scale ═══
const SCALE = [
  { max: 0, color: '#eef2ff', label: '0' },
  { max: 5, color: '#e0e7f8', label: '1–5' },
  { max: 25, color: '#c7d7fe', label: '6–25' },
  { max: 100, color: '#93b4fd', label: '26–100' },
  { max: 300, color: '#6090ea', label: '101–300' },
  { max: Infinity, color: '#1e3a8a', label: '300+' },
]

function getColor(count) {
  if (!count) return SCALE[0].color
  for (const b of SCALE) { if (count <= b.max) return b.color }
  return SCALE[SCALE.length - 1].color
}

// ═══ Geo data cache ═══
const geoCache = {}
async function fetchGeo(url) {
  if (geoCache[url]) return geoCache[url]
  const r = await fetch(url)
  const d = await r.json()
  geoCache[url] = d
  return d
}

const WORLD_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'
const US_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json'
const CA_URL = 'https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/canada.geojson'

const API_BASE = API_BASE_CONFIG

export default function GeoMap({ geography = [], geoType = null, customerId = null, fetchDrilldown = null, workLocations = [] }) {
  const svgRef = useRef(null)
  const wrapRef = useRef(null)
  const tipRef = useRef(null)
  const [hoveredLabel, setHoveredLabel] = useState(null)
  const [drilldownData, setDrilldownData] = useState(null)
  // A failed region read is an error with a Retry, never "No region data" (item 201).
  const [drilldownError, setDrilldownError] = useState(null)
  const [drilldownTry, setDrilldownTry] = useState(0)
  const [loading, setLoading] = useState(false)

  // State click → location drill-down
  const [selectedState, setSelectedState] = useState(null)
  const [stateLocations, setStateLocations] = useState(null)
  const [stateLocLoading, setStateLocLoading] = useState(false)

  const isUSOnly = geoType ? geoType === 'us_states' : geography.every(g => US_STATES_SET.has(g.state))

  // For international: list of countries from geography
  const countries = !isUSOnly ? geography.filter(g => g.state && g.state.length <= 3) : []

  // Default: US-only → USA, international → world overview
  const [selectedCountry, setSelectedCountry] = useState('__init__')
  useEffect(() => {
    if (selectedCountry === '__init__') {
      setSelectedCountry(isUSOnly ? 'USA' : '__world__')
    }
  }, [isUSOnly])

  // Which countries are "drillable" (have state/province data we can fetch)
  const drillable = new Set(['USA', 'CAN'])

  // Active map data for the list panel
  const activeData = selectedCountry === '__world__' ? geography
    : isUSOnly ? geography  // US-only: geography IS the states
    : drilldownData || []   // International: drilldownData has states/provinces for selected country

  const maxCount = activeData[0]?.count || 1

  // Fetch drilldown when country changes (international only)
  useEffect(() => {
    if (selectedCountry === '__init__') return
    if (selectedCountry === '__world__') { setDrilldownData(null); return }
    if (isUSOnly) { setDrilldownData(null); return } // US-only uses geography directly
    if (!fetchDrilldown || !customerId) return

    // International customer: fetch state/province breakdown for selected country
    // This includes USA — we need US states from the locations table
    setLoading(true)
    setDrilldownError(null)
    setSelectedState(null)
    setStateLocations(null)
    fetchDrilldown(customerId, selectedCountry)
      .then(resp => {
        setDrilldownData(resp.regions || [])
        setLoading(false)
      })
      .catch(err => { setDrilldownData([]); setDrilldownError(loadErrorMessage(err)); setLoading(false) })
  }, [selectedCountry, customerId, drilldownTry])

  // Filter work locations for the selected state (client-side, no API call needed)
  useEffect(() => {
    if (!selectedState) { setStateLocations(null); return }
    setStateLocLoading(true)
    // Find locations matching this state:
    // 1. Locations where state field === selectedState (e.g., PA470 with state=PA)
    // 2. Locations where code === selectedState and state is empty (e.g., SC with state="")
    const stateUpper = selectedState.toUpperCase()
    const matching = (workLocations || []).filter(wl => {
      const wlState = (wl.state || '').toUpperCase().trim()
      const wlCode = (wl.code || '').toUpperCase().trim()
      return wlState === stateUpper || ((!wlState) && wlCode === stateUpper)
    })
    // Sort by employee count descending
    matching.sort((a, b) => (b.employee_count || 0) - (a.employee_count || 0))
    const total = matching.reduce((s, m) => s + (m.employee_count || 0), 0)
    const locations = matching.map(m => ({
      code: m.code || '',
      name: m.description || m.code || '',
      city: m.city || '',
      count: m.employee_count || 0,
      pct: total > 0 ? Math.round((m.employee_count || 0) / total * 1000) / 10 : 0,
    }))
    setStateLocations(locations)
    setStateLocLoading(false)
  }, [selectedState, workLocations])

  // ═══ TOOLTIP ═══
  function showTip(event, html) {
    const tip = tipRef.current, wrap = wrapRef.current
    if (!tip || !wrap) return
    const rect = wrap.getBoundingClientRect()
    tip.innerHTML = html; tip.style.opacity = 1
    tip.style.left = (event.clientX - rect.left) + 'px'
    tip.style.top = (event.clientY - rect.top - 14) + 'px'
  }
  function moveTip(event) {
    const tip = tipRef.current, wrap = wrapRef.current
    if (!tip || !wrap) return
    const rect = wrap.getBoundingClientRect()
    tip.style.left = (event.clientX - rect.left) + 'px'
    tip.style.top = (event.clientY - rect.top - 14) + 'px'
  }
  function hideTip() { if (tipRef.current) tipRef.current.style.opacity = 0 }

  function addZoom(svg, g) {
    const zoom = d3.zoom().scaleExtent([1, 8]).on('zoom', e => g.attr('transform', e.transform))
    svg.call(zoom)
    svg.on('dblclick.zoom', () => svg.transition().duration(300).call(zoom.transform, d3.zoomIdentity))
  }

  // ═══ RENDER ═══
  useEffect(() => {
    if (!svgRef.current || !geography.length || selectedCountry === '__init__') return
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    const w = 760

    if (selectedCountry === '__world__' && !isUSOnly) {
      renderWorld(svg, w)
    } else if (selectedCountry === 'USA' || isUSOnly) {
      renderUS(svg, w, isUSOnly ? geography : (drilldownData || []))
    } else if (selectedCountry === 'CAN') {
      renderCanada(svg, w, drilldownData)
    } else {
      // Generic country — show world with highlight
      renderWorld(svg, w)
    }
  }, [selectedCountry, geography, drilldownData])

  // ═══ WORLD MAP ═══
  async function renderWorld(svg, w) {
    const h = 240
    svg.attr('viewBox', `0 0 ${w} ${h}`)
    const g = svg.append('g')
    const dataMap = {}
    geography.forEach(geo => {
      const k = (geo.state || '').trim().toUpperCase()
      dataMap[k] = geo
      if (ISO3_NUM[k]) dataMap[ISO3_NUM[k]] = geo
    })

    try {
      const world = await fetchGeo(WORLD_URL)
      const feats = topojson.feature(world, world.objects.countries).features
      const proj = d3.geoNaturalEarth1().fitSize([w - 20, h - 20], { type: 'Sphere' })
      const path = d3.geoPath().projection(proj)

      g.append('path').datum({ type: 'Sphere' }).attr('d', path).attr('fill', '#f8fafc').attr('stroke', '#e2e8f0')

      g.selectAll('path.c').data(feats).enter().append('path')
        .attr('class', 'geo-map-state')
        .attr('d', path)
        .attr('fill', d => {
          const num = String(d.id).padStart(3, '0')
          const sd = dataMap[num] || (NUM_ISO3[num] && dataMap[NUM_ISO3[num]])
          return getColor(sd?.count || 0)
        })
        .attr('stroke', '#fff').attr('stroke-width', 0.4)
        .on('mouseover', function(ev, d) {
          const name = d.properties?.name || d.id
          const num = String(d.id).padStart(3, '0')
          const a3 = NUM_ISO3[num]
          const sd = dataMap[num] || (a3 && dataMap[a3])
          d3.select(this).attr('stroke', '#374151').attr('stroke-width', 1.2)
          setHoveredLabel(sd?.state || a3 || name)
          showTip(ev, `<strong>${name}</strong><span class="gm-tip-count">${sd ? sd.count.toLocaleString() + ' employees' : '0'}</span>${sd && sd.count > 0 ? '<span class="gm-tip-hint">Click to drill in</span>' : ''}`)
        })
        .on('mousemove', moveTip)
        .on('mouseout', function() {
          d3.select(this).attr('stroke', '#fff').attr('stroke-width', 0.4)
          setHoveredLabel(null); hideTip()
        })
        .on('click', function(ev, d) {
          const num = String(d.id).padStart(3, '0')
          const a3 = NUM_ISO3[num]
          const sd = dataMap[num] || (a3 && dataMap[a3])
          if (sd && sd.count > 0) {
            setSelectedCountry(sd.state || a3)
          }
        })
      addZoom(svg, g)
    } catch (err) { console.error('World map error:', err) }
  }

  // ═══ US STATES ═══
  async function renderUS(svg, w, data) {
    const h = 276
    svg.attr('viewBox', `0 0 ${w} ${h}`)
    const g = svg.append('g')
    const dataMap = {}
    ;(data || []).forEach(d => { dataMap[d.state] = d; dataMap[(d.state||'').toUpperCase()] = d })

    try {
      const us = await fetchGeo(US_URL)
      const feats = topojson.feature(us, us.objects.states).features
      const proj = d3.geoAlbersUsa().fitSize([w - 20, h - 20], { type: 'Sphere' })
      const path = d3.geoPath().projection(proj)

      g.selectAll('path').data(feats).enter().append('path')
        .attr('class', 'geo-map-state')
        .attr('d', path)
        .attr('fill', d => {
          const abbr = FIPS[String(d.id).padStart(2, '0')]
          return getColor(dataMap[abbr]?.count || 0)
        })
        .attr('stroke', '#fff').attr('stroke-width', 0.8)
        .on('mouseover', function(ev, d) {
          const abbr = FIPS[String(d.id).padStart(2, '0')]
          d3.select(this).attr('stroke', '#374151').attr('stroke-width', 1.5)
          setHoveredLabel(abbr)
          const sd = dataMap[abbr]
          showTip(ev, `<strong>${STATE_NAMES[abbr] || abbr}</strong><span class="gm-tip-count">${sd ? sd.count.toLocaleString() + ' · ' + sd.pct + '%' : '0'}</span><span class="gm-tip-hint">Click to see locations</span>`)
        })
        .on('mousemove', moveTip)
        .on('mouseout', function() {
          d3.select(this).attr('stroke', '#fff').attr('stroke-width', 0.8)
          setHoveredLabel(null); hideTip()
        })
        .on('click', function(ev, d) {
          const abbr = FIPS[String(d.id).padStart(2, '0')]
          const sd = dataMap[abbr]
          if (sd && sd.count > 0) {
            setSelectedState(abbr)
          }
        })

      g.append('path')
        .datum(topojson.mesh(us, us.objects.states, (a, b) => a !== b))
        .attr('fill', 'none').attr('stroke', '#fff').attr('stroke-width', 0.8).attr('d', path)
      addZoom(svg, g)
    } catch (err) { console.error('US map error:', err) }
  }

  // ═══ CANADA PROVINCES ═══
  async function renderCanada(svg, w, data) {
    const h = 288
    svg.attr('viewBox', `0 0 ${w} ${h}`)
    const g = svg.append('g')
    const dataMap = {}
    ;(data || []).forEach(d => { dataMap[d.state] = d; dataMap[(d.state||'').toUpperCase()] = d })

    try {
      const canada = await fetchGeo(CA_URL)
      const proj = d3.geoConicConformal().rotate([95, 0]).center([0, 62]).fitSize([w - 40, h - 40], canada)
      const path = d3.geoPath().projection(proj)

      g.selectAll('path').data(canada.features).enter().append('path')
        .attr('class', 'geo-map-state')
        .attr('d', path)
        .attr('fill', d => {
          const abbr = PROVINCE_ABBR[d.properties?.name]
          return getColor(dataMap[abbr]?.count || 0)
        })
        .attr('stroke', '#fff').attr('stroke-width', 0.8)
        .on('mouseover', function(ev, d) {
          const name = d.properties?.name
          const abbr = PROVINCE_ABBR[name] || name
          const sd = dataMap[abbr]
          d3.select(this).attr('stroke', '#374151').attr('stroke-width', 1.5)
          setHoveredLabel(abbr)
          showTip(ev, `<strong>${name}</strong> (${abbr})<span class="gm-tip-count">${sd ? sd.count.toLocaleString() + ' · ' + sd.pct + '%' : '0'}</span>`)
        })
        .on('mousemove', moveTip)
        .on('mouseout', function() {
          d3.select(this).attr('stroke', '#fff').attr('stroke-width', 0.8)
          setHoveredLabel(null); hideTip()
        })
      addZoom(svg, g)
    } catch (err) { console.error('Canada map error:', err) }
  }

  if (!geography || geography.length === 0) return null

  // View label
  const viewLabel = selectedCountry === '__world__'
    ? `${geography.length} Countries`
    : selectedCountry === 'USA'
      ? `United States · ${activeData.length} States`
      : selectedCountry === 'CAN'
        ? `Canada · ${activeData.length} Provinces`
        : `${COUNTRY_NAMES[selectedCountry] || selectedCountry} · ${activeData.length} Regions`

  // State location data for right panel
  const stateData = selectedState ? activeData.find(g => g.state === selectedState) : null

  return (
    <div className="gm-section">
      <div className="gm-map-wrap" ref={wrapRef}>
        <div className="gm-tooltip" ref={tipRef} />

        {/* Country selector — only show for international customers */}
        {!isUSOnly && (
          <div className="gm-country-selector">
            <select
              value={selectedCountry}
              onChange={e => { setSelectedCountry(e.target.value); setSelectedState(null); setStateLocations(null) }}
              className="gm-country-select"
            >
              <option value="__world__">🌍 World Overview</option>
              {countries.map(c => (
                <option key={c.state} value={c.state}>
                  {COUNTRY_NAMES[c.state] || c.state} — {c.count.toLocaleString()}
                  {drillable.has(c.state) ? ' ▸' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading && (
          <div className="gm-loading">Loading regions…</div>
        )}
        {drilldownError && (
          <LoadError inline what="regions" error={drilldownError} onRetry={() => setDrilldownTry(n => n + 1)} />
        )}

        <svg ref={svgRef} className="gm-svg" style={{ width: '100%', height: 'auto' }} />

        <div className="gm-map-footer">
          <div className="gm-legend">
            {SCALE.map((s, i) => (
              <div key={i} className="gm-legend-item">
                <div className="gm-legend-swatch" style={{ background: s.color }} />
                {s.label}
              </div>
            ))}
          </div>
          <div className="gm-zoom-hint">Scroll to zoom · double-click to reset · click state for locations</div>
        </div>
      </div>

      <div className="gm-list-panel">
        {/* State location drill-down view */}
        {selectedState && (isUSOnly || drilldownData) ? (
          <>
            <div className="gm-list-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{STATE_NAMES[selectedState] || PROVINCE_NAMES[selectedState] || selectedState} · Work Locations</span>
              <button
                onClick={() => { setSelectedState(null); setStateLocations(null) }}
                style={{
                  background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4,
                  padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--gray-500)',
                }}
              >← Back</button>
            </div>
            {stateData && (
              <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-100)' }}>
                {stateData.count.toLocaleString()} employees · {stateData.pct}% of workforce
              </div>
            )}
            <div className="gm-list">
              {stateLocLoading && (
                <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>
                  Loading locations…
                </div>
              )}
              {stateLocations && stateLocations.length > 0 && stateLocations.map((loc, i) => (
                <div key={loc.code} className="gm-list-item" style={{ flexWrap: 'wrap', gap: 4 }}>
                  <span className="gm-list-rank">{i + 1}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', minWidth: 36 }}>{loc.code}</span>
                  <span className="gm-list-state" style={{ flex: 1 }} title={loc.name}>{loc.name}{loc.city ? ` · ${loc.city}` : ''}</span>
                  <span className="gm-list-count">{loc.count.toLocaleString()}</span>
                  <span className="gm-list-pct">{loc.pct}%</span>
                </div>
              ))}
              {stateLocations && stateLocations.length === 0 && !stateLocLoading && (
                <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>
                  No work location data for {STATE_NAMES[selectedState] || PROVINCE_NAMES[selectedState] || selectedState}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Default state list view */
          <>
            <div className="gm-list-header">{viewLabel}</div>
            <div className="gm-list">
              {activeData.map((g, i) => (
                <div
                  key={g.state}
                  className={`gm-list-item${hoveredLabel === g.state ? ' gm-list-item-hover' : ''}${g.count > 0 && (isUSOnly || drilldownData) ? ' gm-list-item-clickable' : ''}`}
                  onClick={() => {
                    if (g.count > 0 && (isUSOnly || drilldownData)) {
                      setSelectedState(g.state)
                    }
                  }}
                >
                  <span className="gm-list-rank">{i + 1}</span>
                  <span className="gm-list-state">{STATE_NAMES[g.state] || PROVINCE_NAMES[g.state] || COUNTRY_NAMES[g.state] || g.state}</span>
                  <div className="gm-list-bar-wrap">
                    <div className="gm-list-bar" style={{ width: `${(g.count / maxCount * 100).toFixed(1)}%` }} />
                  </div>
                  <span className="gm-list-count">{g.count.toLocaleString()}</span>
                  <span className="gm-list-pct">{g.pct}%</span>
                </div>
              ))}
              {activeData.length === 0 && !loading && !drilldownError && selectedCountry !== '__world__' && (
                <div className="gm-list-empty">No region data available for this country</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
